import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { BeforeAfterTemplate, BeforeAfterTemplateData } from './templates/BeforeAfterTemplate';
import { MotivationalTemplate, MotivationalTemplateData } from './templates/MotivationalTemplate';
import { generateImageFromElement, downloadImage, copyImageToClipboard } from '../../services/imageGeneratorService';
import { getNutritionistLogo } from '../../services/nutritionistLogoService';
import { useAuth } from '../../contexts/AuthContext';
import './ImageGenerator.css';

export type TemplateType = 'before-after' | 'motivational';

interface ImageGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onImageGenerated?: (imageUrl: string) => void;
}

export const ImageGenerator: React.FC<ImageGeneratorProps> = ({
  isOpen,
  onClose,
  onImageGenerated,
}) => {
  const { user } = useAuth();
  const [templateType, setTemplateType] = useState<TemplateType>('before-after');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  // Before/After Template Data
  const [beforeAfterData, setBeforeAfterData] = useState<BeforeAfterTemplateData>({
    title: 'Transformação',
    description: '',
    metrics: {},
  });

  // Motivational Template Data
  const [motivationalData, setMotivationalData] = useState<MotivationalTemplateData>({
    title: 'Motivação',
    quote: 'Sua jornada começa hoje!',
    backgroundColor: '#667eea',
    hashtags: [],
  });

  useEffect(() => {
    if (isOpen && user) {
      loadLogo();
    }
  }, [isOpen, user]);

  const loadLogo = async () => {
    if (!user?.uid) return;
    try {
      const logo = await getNutritionistLogo(user.uid);
      setLogoUrl(logo);
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
    }
  };

  const handleImageUpload = (field: 'beforeImage' | 'afterImage' | 'backgroundImage', file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;
      if (templateType === 'before-after') {
        setBeforeAfterData(prev => ({
          ...prev,
          [field]: imageUrl,
        }));
      } else {
        setMotivationalData(prev => ({
          ...prev,
          backgroundImage: imageUrl,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateImage = async () => {
    if (!templateRef.current) return;

    try {
      setGenerating(true);
      const imageDataUrl = await generateImageFromElement(templateRef.current, {
        width: 1920,
        height: 1920,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      if (onImageGenerated) {
        onImageGenerated(imageDataUrl);
      }

      alert('Imagem gerada com sucesso! Use os botões abaixo para compartilhar ou baixar.');
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      alert('Erro ao gerar imagem. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!templateRef.current) return;

    try {
      setGenerating(true);
      const imageDataUrl = await generateImageFromElement(templateRef.current, {
        width: 1920,
        height: 1920,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      const filename = `nutrisync-${templateType}-${Date.now()}.jpg`;
      downloadImage(imageDataUrl, filename);
    } catch (error) {
      console.error('Erro ao baixar imagem:', error);
      alert('Erro ao baixar imagem. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  };

  const handleShareInstagram = async () => {
    if (!templateRef.current) return;

    try {
      setGenerating(true);
      const imageDataUrl = await generateImageFromElement(templateRef.current, {
        width: 1920,
        height: 1920,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      downloadImage(imageDataUrl, 'nutrisync-instagram.jpg');
      // Compartilhamento no Instagram não está disponível via web
      // O usuário pode baixar a imagem e compartilhar manualmente
      alert('Para compartilhar no Instagram, baixe a imagem e compartilhe pelo app do Instagram.');
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!templateRef.current) return;

    try {
      setGenerating(true);
      const imageDataUrl = await generateImageFromElement(templateRef.current, {
        width: 1920,
        height: 1920,
        scale: 2,
        backgroundColor: '#ffffff',
      });

      await copyImageToClipboard(imageDataUrl);
      alert('Imagem copiada para a área de transferência!');
    } catch (error) {
      console.error('Erro ao copiar imagem:', error);
      alert('Não foi possível copiar a imagem. Tente baixar a imagem.');
    } finally {
      setGenerating(false);
    }
  };

  const getCurrentTemplateData = () => {
    if (templateType === 'before-after') {
      return { ...beforeAfterData, logoUrl: logoUrl || undefined };
    } else {
      return { ...motivationalData, logoUrl: logoUrl || undefined };
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gerador de Imagens Personalizadas"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button onClick={handleGenerateImage} disabled={generating}>
            {generating ? 'Gerando...' : 'Gerar Imagem'}
          </Button>
        </>
      }
    >
      <div className="image-generator">
        {/* Template Selection */}
        <div className="generator-section">
          <label className="generator-label">Selecione o Template</label>
          <div className="template-selector">
            <button
              type="button"
              className={`template-option ${templateType === 'before-after' ? 'active' : ''}`}
              onClick={() => setTemplateType('before-after')}
            >
              <span className="template-icon">📊</span>
              <span className="template-name">Antes e Depois</span>
            </button>
            <button
              type="button"
              className={`template-option ${templateType === 'motivational' ? 'active' : ''}`}
              onClick={() => setTemplateType('motivational')}
            >
              <span className="template-icon">💪</span>
              <span className="template-name">Motivacional</span>
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="generator-form">
          {templateType === 'before-after' ? (
            <>
              <div className="form-row">
                <label className="generator-field">
                  <span>Título</span>
                  <input
                    type="text"
                    className="ns-input"
                    value={beforeAfterData.title || ''}
                    onChange={(e) => setBeforeAfterData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Transformação"
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="generator-field">
                  <span>Descrição</span>
                  <textarea
                    className="ns-textarea"
                    rows={3}
                    value={beforeAfterData.description || ''}
                    onChange={(e) => setBeforeAfterData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva a transformação..."
                  />
                </label>
              </div>

              <div className="form-row form-row-2">
                <label className="generator-field">
                  <span>Imagem Antes</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload('beforeImage', file);
                    }}
                  />
                </label>
                <label className="generator-field">
                  <span>Imagem Depois</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload('afterImage', file);
                    }}
                  />
                </label>
              </div>

              <div className="form-row form-row-3">
                <label className="generator-field">
                  <span>Peso Antes (kg)</span>
                  <input
                    type="number"
                    step="0.1"
                    className="ns-input"
                    value={beforeAfterData.metrics?.weightBefore || ''}
                    onChange={(e) => setBeforeAfterData(prev => ({
                      ...prev,
                      metrics: { ...prev.metrics, weightBefore: parseFloat(e.target.value) || undefined },
                    }))}
                    placeholder="Ex: 80.5"
                  />
                </label>
                <label className="generator-field">
                  <span>Peso Depois (kg)</span>
                  <input
                    type="number"
                    step="0.1"
                    className="ns-input"
                    value={beforeAfterData.metrics?.weightAfter || ''}
                    onChange={(e) => setBeforeAfterData(prev => ({
                      ...prev,
                      metrics: { ...prev.metrics, weightAfter: parseFloat(e.target.value) || undefined },
                    }))}
                    placeholder="Ex: 70.0"
                  />
                </label>
                <label className="generator-field">
                  <span>Medidas</span>
                  <input
                    type="text"
                    className="ns-input"
                    value={beforeAfterData.metrics?.measurements || ''}
                    onChange={(e) => setBeforeAfterData(prev => ({
                      ...prev,
                      metrics: { ...prev.metrics, measurements: e.target.value || undefined },
                    }))}
                    placeholder="Ex: 90-70-95"
                  />
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="form-row">
                <label className="generator-field">
                  <span>Título</span>
                  <input
                    type="text"
                    className="ns-input"
                    value={motivationalData.title || ''}
                    onChange={(e) => setMotivationalData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Motivação"
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="generator-field">
                  <span>Frase Motivacional</span>
                  <textarea
                    className="ns-textarea"
                    rows={4}
                    value={motivationalData.quote || ''}
                    onChange={(e) => setMotivationalData(prev => ({ ...prev, quote: e.target.value }))}
                    placeholder="Ex: Sua jornada começa hoje!"
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="generator-field">
                  <span>Imagem de Fundo (Opcional)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload('backgroundImage', file);
                    }}
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="generator-field">
                  <span>Cor de Fundo</span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={motivationalData.backgroundColor || '#667eea'}
                      onChange={(e) => setMotivationalData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      style={{ width: '60px', height: '40px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      className="ns-input"
                      value={motivationalData.backgroundColor || '#667eea'}
                      onChange={(e) => setMotivationalData(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      placeholder="#667eea"
                      style={{ flex: 1 }}
                    />
                  </div>
                </label>
              </div>

              <div className="form-row">
                <label className="generator-field">
                  <span>Hashtags (separadas por vírgula)</span>
                  <input
                    type="text"
                    className="ns-input"
                    value={motivationalData.hashtags?.join(', ') || ''}
                    onChange={(e) => {
                      const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
                      setMotivationalData(prev => ({ ...prev, hashtags: tags }));
                    }}
                    placeholder="Ex: nutrição, saúde, bemestar"
                  />
                </label>
              </div>
            </>
          )}
        </div>

        {/* Preview */}
        <div className="generator-section">
          <label className="generator-label">Preview</label>
          <div className="template-preview-container">
            <div ref={templateRef} className="template-preview">
              {templateType === 'before-after' ? (
                <BeforeAfterTemplate data={getCurrentTemplateData() as BeforeAfterTemplateData} />
              ) : (
                <MotivationalTemplate data={getCurrentTemplateData() as MotivationalTemplateData} />
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="generator-actions">
          <Button onClick={handleDownload} disabled={generating} variant="secondary">
            📥 Download
          </Button>
          <Button onClick={handleShareInstagram} disabled={generating} variant="secondary">
            📷 Instagram
          </Button>
          <Button onClick={handleCopyToClipboard} disabled={generating} variant="secondary">
            📋 Copiar
          </Button>
        </div>

        {!logoUrl && (
          <div className="generator-warning">
            ⚠️ Você ainda não tem um logo configurado. Configure seu logo nas <strong>Configurações</strong> para aparecer nas imagens.
          </div>
        )}
      </div>
    </Modal>
  );
};



